import * as yup from 'yup';

import {
  Customer,
  CustomerBusinessOperationsCustomersPatchRequest,
  CustomerBusinessOperationsFundsSourcePatchRequest,
  CustomerBusinessOperationsProfilePatchRequest,
  CustomerCompanyDetailsPatchRequest,
  CustomerOrganizationContactPatchRequest,
  CustomerOrganizationLegalRepresentativePatchRequest,
  CustomerOrganizationOwnersPatchRequest,
  CustomerPatchRequest,
  CustomerSliceType,
  CustomerSocialNetworkPatchRequest,
  patchBusinessInfo,
} from '@/types/customer';
import { copyWith } from '@/utility/copyWith';

export type SaveFn = (
  type: CustomerSliceType,
  data: unknown,
  errors: Record<string, yup.ValidationError> | null,
) => void;

export const previewPatch = (
  customer: Customer | undefined,
  patchRequest: CustomerPatchRequest,
): Customer | any => {
  if (customer) {
    return {
      ...customer,
      businessInfo: patchBusinessInfo(customer, patchRequest),
    };
  } else {
    return {};
  }
};

export const toPatchRequest = (type: CustomerSliceType, data: any): CustomerPatchRequest => {
  switch (type) {
    case CustomerSliceType.businessOperationsProfile:
      return {
        type: CustomerSliceType.businessOperationsProfile,
        countries: data.countries,
        industry: data.industry,
        hasBrazilLicenseRequirement: !!data.hasBrazilLicenseRequirement,
        isFinancialInstitution: !!data.isFinancialInstitution,
        services: data.services,
      } satisfies CustomerBusinessOperationsProfilePatchRequest;
    case CustomerSliceType.socialNetwork:
      return {
        type: CustomerSliceType.socialNetwork,
        website: data.website,
        socialMediaNetworks: data.socialMediaNetworks,
      } satisfies CustomerSocialNetworkPatchRequest;
    case CustomerSliceType.organizationOwners:
      return {
        type: CustomerSliceType.organizationOwners,
        owners: data.owners?.map((owner: any): any =>
          copyWith(owner, {
            taxId: unformat(owner.taxId, 'taxId'),
            address:
              owner.address !== null
                ? copyWith(owner.address, { zipCode: numberOrNull(owner.address.zipCode) })
                : null,
            annualIncome: owner.annualIncom !== null ? numberOrNull(owner.annualIncome) : null,
            share: owner.share !== null ? numberOrNull(owner.share) : null,
          }),
        ),
      } satisfies CustomerOrganizationOwnersPatchRequest;
    case CustomerSliceType.companyDetails:
      return {
        type: CustomerSliceType.companyDetails,
        entityType: data.entityType,
        commercialName: data.commercialName,
        taxId: unformat(data.taxId, 'taxId'),
        legalEntityName: data.legalEntityName,
        operationStartDate: data.operationStartDate,
        address: data.address
          ? {
              streetAddress: data.address.streetAddress ?? null,
              state: data.address.state ?? null,
              country: data.address.country ?? null,
              complement: data.address.complement ?? null,
              city: data.address.city ?? null,
              zipCode: numberOrNull(data.address?.zipCode),
            }
          : null,
      } satisfies CustomerCompanyDetailsPatchRequest;
    case CustomerSliceType.businessOperationsCustomers:
      return {
        type: CustomerSliceType.businessOperationsCustomers,
        customerType: data.customerType,
        quantity: numberOrNull(data.quantity),
        countries: data.countries,
        industries: data.industries ?? null,
        typicalCustomer: data.typicalCustomer,
      } satisfies CustomerBusinessOperationsCustomersPatchRequest;
    case CustomerSliceType.businessOperationsFundsSource:
      return {
        type: CustomerSliceType.businessOperationsFundsSource,
        thirdPartyBeneficiaries: !!data.thirdPartyBeneficiaries,
        usdVolume: data.usdVolume,
      } satisfies CustomerBusinessOperationsFundsSourcePatchRequest;
    case CustomerSliceType.organizationContact:
      return {
        type: CustomerSliceType.organizationContact,
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phoneNumber: unformat(data.phoneNumber, 'phone'),
        position: data.position,
      } satisfies CustomerOrganizationContactPatchRequest;
    case CustomerSliceType.organizationLegalRepresentative:
      return {
        type: CustomerSliceType.organizationLegalRepresentative,
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phoneNumber: unformat(data.phoneNumber, 'phone'),
      } satisfies CustomerOrganizationLegalRepresentativePatchRequest;
    default:
      throw new Error(`not really a patch operation ${type}`);
  }
};

export const numberOrNull = (value: string | null): number | null => {
  if (value === null || value === undefined) {
    return null;
  }

  const numeric = Number(value);
  if (isNaN(numeric)) {
    return null;
  }

  return numeric;
};

const unformat = (value: string | null, type: 'phone' | 'taxId'): string | null => {
  if (value === null || value === undefined) {
    return null;
  }

  switch (type) {
    case 'phone':
      return String(value).replace(/[^0-9+]/g, '');
    case 'taxId':
      return String(value).replace(/[^0-9]/g, '');
    default:
      return value;
  }
};
